// src/pages/admins/projectWorkspaceUtils.js
//
// Shared formatting/color helpers for the Project-Type-Driven Project
// Workspace (Overview/Process/Board/Timeline/Milestones/Activity tabs) --
// kept in one place so the same project/phase/milestone status -> label/
// tone mapping isn't reimplemented per tab.
//
// IMPORTANT: these enums are exactly what the locked backend contract
// returns -- never invent a value that isn't listed here. Kept in sync with
// services/projectWorkspaceService.js's PHASE_STATUS/PHASE_STATUS_LABELS
// (backend) -- Phase 47 added AT_RISK and BLOCKED to both the phase enum
// AND the project timeline-health enum (see deriveProjectHealthStatus()'s
// severity order: DELAYED > BLOCKED > AT_RISK > ON_TRACK, COMPLETED
// overrides all).
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

export const PROJECT_STATUS_META = {
  ON_TRACK: { label: "On Track", tone: "success", icon: "fa-circle-check" },
  DELAYED: { label: "Delayed", tone: "danger", icon: "fa-triangle-exclamation" },
  AT_RISK: { label: "At Risk", tone: "warning", icon: "fa-triangle-exclamation" },
  BLOCKED: { label: "Blocked", tone: "dark", icon: "fa-ban" },
  COMPLETED: { label: "Completed", tone: "primary", icon: "fa-flag-checkered" },
};

// Timeline phase-status badges (task 3). `icon` here is the plain-text/
// unicode glyph the spec asks for ("Do not rely on color alone: always
// show text/icon") -- rendered alongside the label, not a font-icon class
// (unlike PROJECT_STATUS_META's fa-* icons), so it reads even where
// Font Awesome isn't loaded.
export const PHASE_STATUS_META = {
  COMPLETED: { label: "Completed", tone: "success", icon: "✓" },
  IN_PROGRESS: { label: "In Progress", tone: "info", icon: "●" },
  DELAYED: { label: "Delayed", tone: "danger", icon: "!" },
  AT_RISK: { label: "At Risk", tone: "warning", icon: "▲" },
  NOT_STARTED: { label: "Not Started", tone: "secondary", icon: "○" },
  BLOCKED: { label: "Blocked", tone: "dark", icon: "⛔" },
};

// Same 6 statuses, in the fixed display order the Timeline legend (task 4)
// and any other "show every possible status" UI should use.
export const PHASE_STATUS_ORDER = ["COMPLETED", "IN_PROGRESS", "AT_RISK", "DELAYED", "NOT_STARTED", "BLOCKED"];

// Kept in sync with the backend's MILESTONE_STATUS/MILESTONE_STATUS_LABELS
// (services/projectWorkspaceService.js) -- never invent a value on one side
// that isn't listed on the other. A milestone completed after its planned
// date is COMPLETED_LATE ("Completed Late"), distinct from OVERDUE (no
// actual_date yet, planned_date already passed) -- these must never share a
// label/badge, or a completed-late milestone reads as still pending.
export const MILESTONE_STATUS_META = {
  COMPLETED_ON_TIME: { label: "Completed", tone: "success" },
  COMPLETED_LATE: { label: "Completed Late", tone: "warning" },
  OVERDUE: { label: "Overdue", tone: "danger" },
  DUE_TODAY: { label: "Due Today", tone: "warning" },
  UPCOMING: { label: "Upcoming", tone: "info" },
  // Phase 90: every linked ticket is done but no actual_date is recorded yet.
  READY_TO_COMPLETE: { label: "Ready to Complete", tone: "primary" },
};

export function statusMeta(map, status) {
  return map[status] || { label: status ? String(status).replace(/_/g, " ") : "Unknown", tone: "secondary" };
}

export function formatDate(value) {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d.format("DD MMM YYYY") : null;
}

export function formatDateOrDash(value) {
  return formatDate(value) || "—";
}

export function formatDateOrNotScheduled(value) {
  return formatDate(value) || "Not scheduled";
}

export function timeAgo(value) {
  if (!value) return "—";
  const d = dayjs(value);
  return d.isValid() ? d.fromNow() : "—";
}

export const ACTIVITY_SOURCE_META = {
  STATUS: { icon: "fa-flag", label: "Status Change" },
  ASSIGNMENT: { icon: "fa-user-check", label: "Assignment" },
  REMARK: { icon: "fa-comment", label: "Remark" },
  PROJECT: { icon: "fa-diagram-project", label: "Project" },
  PHASE: { icon: "fa-list-check", label: "Phase" },
  MILESTONE: { icon: "fa-flag-checkered", label: "Milestone" },
};

export function toneBadgeClass(tone) {
  // Maps a bootstrap-style tone name to this app's "unit-status"-family
  // badge classes are page-specific, so callers use react-bootstrap's
  // <Badge bg={tone}> directly -- this helper exists only for the rare
  // custom chip that needs a raw class name instead of a <Badge>.
  return `pw-chip pw-chip-${tone}`;
}
